<template>
  <div>
    <apexchart :options="chartOptions" :series="chartSeries" height="400"  v-if="!isLoading"></apexchart>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import api from "../../services/api";
import frLocale from 'apexcharts/dist/locales/fr.json'

export default {
  name: 'TreasuryGlobalColumn',

  components: {
    apexchart: VueApexCharts,
  },

  props: {
    cardValues: {
      type: Array
    },
    isGlobal:{
      required:false,
      type:Boolean,
      default:false,
    },
    operationId: {
      default: null
    },
    startOfYear: {
      type:Number,
      default: null
    },
    endOfYear: {
      type:Number,
      default: null
    },
  },

  data() {
    return {
      operationName:"",
      isLoading: false,
      societeMovements: [],
      investorMovements: [],
      totalMovements: [],
      emprunts: [],
      selectedYear: new Date().getFullYear(),
      chartOptions: {
        dataLabels: {
          enabled: false
        },
        annotations: {
          xaxis: [
            {
              x: new Date().getTime(),
              borderColor: '#000000',
              label: {
                borderColor: '#B0BFE8',
                orientation: 'horizontal',
                position: 'top',
                style: {
                  background: "#B0BFE8",
                  color: "#07153C",
                  fontWeight: 'bold',
                  fontSize: '12px'
                },
                text: new Date().toLocaleDateString()
              }
            }
          ]
        },
        chart: {
          id: 'treasury-global-column',
          locales: [frLocale],
          defaultLocale: 'fr',
          stacked: false,  // Active le mode empilé
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 400,
            animateGradually: {
              enabled: true,
              delay: 150
            },
            dynamicAnimation: {
              enabled: true,
              speed: 350
            }
          },
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true,
            },
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '80%',
          },
        },
        stroke: {
          show: true,
          width: this.getStrokeWidths(), // Appelle la fonction pour générer le tableau de width
          curve: 'stepline',
        },
        xaxis: {
          type: 'category', // Changer de 'datetime' à 'category'
          categories: [
            'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
          ], // Catégories pour les mois
          labels: {
            formatter: function (value) {
              return value; // Afficher les abréviations de mois définies dans 'categories'
            }
          },
          tooltip: {
            enabled: false,
          },
        },
        yaxis: {
          title: {
            text: this.$t('treasury.chart.yaxis.label'),
          },
        },
        markers: {
          size: 4,
          strokeWidth: 0,
          hover: {
            sizeOffset: 1,
          },
        },
        tooltip: {
          x: {
            show: true,
            format: "dd MMM yyyy",
            formatter: function (value, {series, seriesIndex, dataPointIndex, w}) {
              return new Date(value).toLocaleDateString('fr-fr', {
                weekday: "long",
                year: "numeric",
                month: "short",
                day: "numeric"
              })
            },
          },
          shared: false,
          custom: this.customTooltip
        },
        colors: ['#6D84C4', '#06C9B3', '#9E0430','#3A4D80'],
      },
      chartSeries: [
        {
          name: this.$t('treasury.chart.societe'),
          data: [],
          type: 'bar',
        },
        {
          name: this.$t('treasury.chart.investor.global'),
          data: [],
          type: 'bar',
        },
        {
          name: this.$t('treasury.chart.emprunts.global'),
          data: [],
          type: 'bar',
        },
        {
          name: this.$t('treasury.chart.societe.global'),
          data: [],
          type: 'line',
        }
      ],
    };
  },

  emits: ['updateCardValues'],

  async mounted() {
    await this.fetchData()
  },

  watch: {
    chartSeries: {
      handler: function (newVal) {
        this.updateColorSeries();
      },
      deep: true,
    },
  },
  methods: {

    getStrokeWidths() {
      const seriesCount = this.chartSeries ? this.chartSeries .length : 0;
      const widths = new Array(seriesCount).fill(1); // Remplit un tableau avec 1 pour toutes les séries
      if (seriesCount > 0) {
        widths[seriesCount - 1] = 2; // Définit la largeur à 3 pour la dernière série
      }
      return widths;
    },

    async fetchData(){
      this.isLoading = true
      if(this.operationId){
        await this.fetchOperation()
      }
      await this.fetchTreasuryMovements();
      await this.updateChartSeries();
      this.updateColorSeries();
      this.chartOptions.stroke.width = this.getStrokeWidths();
      this.isLoading = false
    },

    async fetchOperation() {
      await api
        .get(`/operation/${this.operationId}`)
        .then(response => {
          this.operationName = response.data.name
        })
    },

    async fetchTreasuryMovements() {
      try {
        let url = "/treasury/getAllTreasuryMovementAndEvenement";
        if (this.isGlobal && this.operationId) {
          url = `/treasury/getAllTreasuryMovementAndEvenementForOperation/${this.operationId}`;
        }
        if(url === "/treasury/getAllTreasuryMovementAndEvenement"){
          url += `?startOfYear=${this.startOfYear}&endOfYear=${this.endOfYear}`
        }
        let response = await api.get(url);

        response = JSON.parse(JSON.stringify(response.data));

        let baseMovements = [];
        let index = 0

        baseMovements.push({ data: this.formatDate(response.societe) });

        index++

        if (response.investor) {
          baseMovements.push({ data: this.formatDate(response.investor) });
          this.chartSeries[index].data = [];
          index++
        }
        if (response.emprunts) {
          baseMovements.push({ data: this.formatDate(response.emprunts) });
          this.chartSeries[index].data = [];
          index++
        }

        baseMovements.push({ data: this.formatDate(response.societeWithTreasury) });
        this.chartSeries[index].data = [];

        baseMovements.forEach((movement, i) => {
          if(i === baseMovements.length - 1){
            this.chartSeries[i].data = this.formatDataForChartSoldeTreasury(movement.data);
          }
          else {
            this.chartSeries[i].data = this.formatDataForChart(movement.data);
          }
        });

        // Vérifier si startOfYear est dans l'année courante
        const currentYear = new Date().getFullYear();
        const startYear = new Date(this.startOfYear).getFullYear();

        if (startYear === currentYear) {
          this.$emit('updateCardValues', response.cardValues);
        }
      } catch (error) {
        console.error(error);
      }
    },

    formatDataForChart(treasuryMovements) {
      if (!treasuryMovements) return [];

      // Utiliser les abréviations anglaises des mois pour les clés
      const monthlyData = {
        "Jan": {solde: 0, expenses: []},
        "Feb": {solde: 0, expenses: []},
        "Mar": {solde: 0, expenses: []},
        "Apr": {solde: 0, expenses: []},
        "May": {solde: 0, expenses: []},
        "Jun": {solde: 0, expenses: []},
        "Jul": {solde: 0, expenses: []},
        "Aug": {solde: 0, expenses: []},
        "Sep": {solde: 0, expenses: []},
        "Oct": {solde: 0, expenses: []},
        "Nov": {solde: 0, expenses: []},
        "Dec": {solde: 0, expenses: []}
      };

      treasuryMovements
        .filter(movement => {
          // Filtrer les mouvements pour ne garder que ceux qui sont dans la plage de temps définie
          if(movement && movement.date){
            const timestamp = new Date(movement.date).getTime();
            return timestamp >= this.startOfYear && timestamp < this.endOfYear;
          }
          return null
        })
        .forEach(movement => {
          const date = new Date(movement.date);
          const month = date.toLocaleString('en-US', {month: 'short'}); // Utiliser l'abréviation anglaise

          if (this.operationId && movement.expense) {
            movement.expense = movement.expense.map(expense => {
              return expense ? {
                ...expense,
                name: expense.name.replaceAll(`- ${this.operationName}`, '')
              } : null;
            });
          }

          // Ajouter le solde au mois correspondant
          monthlyData[month].solde += Math.trunc(movement.montant)
          // Ajouter les expenses au mois correspondant
          monthlyData[month].expenses = monthlyData[month].expenses.concat(movement.expense || []);
          monthlyData[month].expenses.sort((a, b) => a.name.localeCompare(b.name));
        });

      // Convertir les données pour correspondre aux catégories de l'axe X
      return Object.keys(monthlyData).map(month => ({
        x: month,
        y: monthlyData[month].solde,
        expenses: monthlyData[month].expenses
      }));
    },

    formatDataForChartSoldeTreasury(treasuryMovements) {
      if (!treasuryMovements) return [];

      // Utiliser les abréviations anglaises des mois pour les clés
      let monthlyData = {
        "Jan": {solde: 0, expenses: []},
        "Feb": {solde: 0, expenses: []},
        "Mar": {solde: 0, expenses: []},
        "Apr": {solde: 0, expenses: []},
        "May": {solde: 0, expenses: []},
        "Jun": {solde: 0, expenses: []},
        "Jul": {solde: 0, expenses: []},
        "Aug": {solde: 0, expenses: []},
        "Sep": {solde: 0, expenses: []},
        "Oct": {solde: 0, expenses: []},
        "Nov": {solde: 0, expenses: []},
        "Dec": {solde: 0, expenses: []}
      };

      const monthOrder = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];


      // Filtrer les mouvements pour ne garder que ceux de l'année courante
      const currentYear = new Date(this.startOfYear).getFullYear();
      const currentYearMovements = treasuryMovements
        .filter(movement => {
          if(movement && movement.date){
            const timestamp = new Date(movement.date).getTime();
            return timestamp >= this.startOfYear && timestamp <= this.endOfYear;
          }
          return null
        })

      let lastKnownSoldeForLastYear = null; // Pour garder le dernier solde trouvé, même si des années précédentes

      // Rechercher le dernier solde connu dans les années précédentes si nécessaire
      function getLastKnownSoldeBeforeCurrentYear() {
        for (let i = treasuryMovements.length - 1; i >= 0; i--) {
          const movement = treasuryMovements[i];
          const movementDate = new Date(movement.date);
          const movementYear = movementDate.getFullYear();

          // Si on trouve un mouvement avant l'année courante, on retourne son solde
          if (movementYear < currentYear) {
            return Math.trunc(movement.solde);
          }
        }
        return null;
      }

      // Si on n'a pas de mouvement pour l'année courante, chercher le dernier solde avant cette année
      lastKnownSoldeForLastYear = getLastKnownSoldeBeforeCurrentYear();

      // Traiter les mouvements de l'année courante
      currentYearMovements.forEach(movement => {
        const date = new Date(movement.date);
        const month = date.toLocaleString('en-US', { month: 'short' }); // Utiliser l'abréviation anglaise

        if (this.operationId && movement.expense) {
          movement.expense = movement.expense.map(expense => {
            return expense ? {
              ...expense,
              name: expense.name.replaceAll(`- ${this.operationName}`, '')
            } : null;
          });
        }

        // Mettre à jour le solde du mois courant
        monthlyData[month].solde = Math.trunc(movement.solde);
        // Ajouter les expenses du mois
        monthlyData[month].expenses = monthlyData[month].expenses.concat(movement.expense || []);
        monthlyData[month].expenses.sort((a, b) => a.name.localeCompare(b.name));

      });

      if(monthlyData[monthOrder[0]].solde === 0 && monthlyData[monthOrder[0]].expenses.length === 0){
        monthlyData[monthOrder[0]].solde = lastKnownSoldeForLastYear ? lastKnownSoldeForLastYear : 0;
      }

      let lastKnownSolde = null;
      // Remplir les mois sans solde dans l'année courante en utilisant le dernier solde connu
      monthOrder.forEach(month => {
        if (monthlyData[month].solde === 0 && monthlyData[month].expenses.length === 0) {
          // Utiliser le dernier solde connu si le mois actuel n'a pas de mouvement
          monthlyData[month].solde = lastKnownSolde ? lastKnownSolde : 0;
        } else{
          lastKnownSolde = monthlyData[month].solde;
        }
      });

      let objToReturn = monthOrder.map(month => ({
        x: month,
        y: monthlyData[month].solde,
        expenses: monthlyData[month].expenses
      }));

      return objToReturn
    },






    formatDate(object) {
      if (!object || object.length === 0) return []
      return object.map(movement => {
        if (!movement || !movement.date) return
        const date = new Date(movement.date);
        return {...movement, date: date, expenses: [movement.expense]};
      })
    },

    updateChartSeries() {
      this.chartSeries = [...this.chartSeries];

    },
    updateColorSeries() {
      switch (this.chartSeries.length) {
        case 1:
          this.chartOptions.colors = ['#6D84C4'];
          break;
        case 2:
          if (this.chartSeries[1].name === this.$t('treasury.chart.investor.global')) {
            this.chartOptions.colors = ['#6D84C4', '#06C9B3'];
          } else {
            this.chartOptions.colors = ['#6D84C4', '#9E0430'];
          }
          break;
        case 3:
          this.chartOptions.colors = ['#6D84C4', '#06C9B3', '#9E0430'];
          break;
        case 4:
          this.chartOptions.colors = ['#6D84C4', '#06C9B3', '#9E0430', '#3A4D80'];
          break;
        default:
          this.chartOptions.colors = ['#6D84C4', '#06C9B3', '#9E0430','#3A4D80'];
          break;
      }
      this.chartOptions = { ...this.chartOptions };
    },


    customTooltip({series, seriesIndex, dataPointIndex, w}) {
      const currentData = this.chartSeries[seriesIndex].data[dataPointIndex];
      const monthAbbreviation = currentData.x; // Le mois abrégé en anglais
      const solde = currentData.y; // Le solde pour ce mois
      let expenses = currentData.expenses || []; // Les dépenses pour ce mois

      // Table de correspondance entre les abréviations anglaises et les noms de mois en francais
      const monthMap = {
        "Jan": "Janvier",
        "Feb": "Février",
        "Mar": "Mars",
        "Apr": "Avril",
        "May": "Mai",
        "Jun": "Juin",
        "Jul": "Juillet",
        "Aug": "Août",
        "Sep": "Septembre",
        "Oct": "Octobre",
        "Nov": "Novembre",
        "Dec": "Décembre"
      };

      let expensesDetails = "";

      const month = monthMap[monthAbbreviation];


      expenses.forEach(expense => {
        if (expense && expense.name) {
          expensesDetails += `<div class="d-flex justify-content-between"><span>${expense.name}:</span><span><strong>${expense.type === "sortant" ? ' ' : ' +'}${Intl.NumberFormat().format(expense.value)}€</strong></span></div>`;
        }
      });

      return `
    <div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
      ${month}
    </div>
    <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex; min-width: 300px;">
                  <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 13px;min-width: 300px; border-left: 3px solid ${w.globals.markers.colors[seriesIndex]}; padding-left: 6px;">
                      <div class="apexcharts-tooltip-y-group" style="display: flex; width: 100%; justify-content: space-between;" >
                          <span class="apexcharts-tooltip-text-y-label">
                              <b>${w.globals.seriesNames[seriesIndex]}:</b>
                          </span>
                          <span class="apexcharts-tooltip-text-y-value" >
                              ${Intl.NumberFormat().format(solde)}€
                          </span>
                      </div>
                      <div class="additional-content" style="margin-bottom: 0.5em;font-size: 12px;"">
                            ${expensesDetails}
                      </div>
                  </div>
              </div>
  `;
    },
  },
};
</script>

<style scoped>

</style>




// WEBPACK FOOTER //
// src/views/treasury/TreasuryGlobalColumn.vue